import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

// Destination names are proper nouns — do not translate (matches glossary)
const DESTINATIONS = ["Ibiza", "Mykonos", "Dubai", "Miami", "Tulum", "Bali", "Marbella", "Monaco", "Saint-Tropez", "Cannes", "Courchevel", "Other"];

export default function CustomTripPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    destinations: ["Ibiza"],
    start_date: "",
    end_date: "",
    flexible_dates: false,
    guests: 4,
    budget_min: 5000,
    budget_max: 25000,
    currency: "EUR",
    preferred_payment_method: "crypto",
    trip_style: ["Beach & yacht"],
    requested_services: ["Villa", "Yacht", "Driver"],
    raw_user_input: "",
    contact_email: "",
    contact_phone: "",
  });
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const TRIP_STYLES = useMemo(() => [
    { value: "Honeymoon",     label: t("custom_trip.style_honeymoon", "Honeymoon") },
    { value: "Birthday",      label: t("custom_trip.style_birthday",  "Birthday") },
    { value: "Bachelor",      label: t("custom_trip.style_bachelor",  "Bachelor/ette") },
    { value: "Family",        label: t("custom_trip.style_family",    "Family") },
    { value: "Wellness",      label: t("custom_trip.style_wellness",  "Wellness") },
    { value: "Nightlife",     label: t("custom_trip.style_nightlife", "Nightlife") },
    { value: "Yachting",      label: t("custom_trip.style_yachting",  "Yachting") },
    { value: "Low-key",       label: t("custom_trip.style_discreet",  "Low-key & discreet") },
    { value: "Corporate",     label: t("custom_trip.style_corporate", "Corporate / off-site") },
  ], [t]);

  const SERVICES = useMemo(() => [
    { value: "Villa",         label: t("custom_trip.svc_villa",    "Villa") },
    { value: "Yacht",         label: t("custom_trip.svc_yacht",    "Yacht") },
    { value: "Driver",        label: t("custom_trip.svc_driver",   "Driver / car") },
    { value: "Clubs",         label: t("custom_trip.svc_clubs",    "Clubs / VIP tables") },
    { value: "Chef",          label: t("custom_trip.svc_chef",     "Private chef") },
    { value: "Wellness",      label: t("custom_trip.svc_wellness", "Wellness / spa") },
    { value: "Photographer",  label: t("custom_trip.svc_photo",    "Photographer") },
    { value: "Security",      label: t("custom_trip.svc_security", "Security") },
    { value: "Private jet",   label: t("custom_trip.svc_jet",      "Private jet") },
  ], [t]);

  const PAYMENT = useMemo(() => [
    { value: "crypto", label: t("custom_trip.payment_crypto", "Crypto (USDT / USDC)") },
    { value: "card",   label: t("custom_trip.payment_card",   "Card") },
    { value: "bank",   label: t("custom_trip.payment_bank",   "Bank transfer") },
    { value: "unknown", label: t("common.all", "Anything") },
  ], [t]);

  const toggle = (field, val) => {
    setForm((f) => {
      const arr = f[field].includes(val) ? f[field].filter((x) => x !== val) : [...f[field], val];
      return { ...f, [field]: arr };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload = { ...form };
      ["contact_email", "contact_phone", "start_date", "end_date", "raw_user_input"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      await api.post("/custom-trips", payload);
      setDone(true);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="pt-32 px-5 md:px-10 mx-auto max-w-2xl text-center">
        <div className="font-script text-5xl text-teal mb-4">{t("custom_trip.thanks_title", "Received.")}</div>
        <h1 className="section-title text-4xl md:text-5xl mb-6">{t("dashboard.admin_title", "Your concierge is on it.")}</h1>
        <p className="text-muted">{t("custom_trip.thanks_subtitle", "Our concierge will revert within hours with curated options.")}</p>
        <div className="flex justify-center gap-3 mt-10">
          <Link to="/" className="btn btn-outline">{t("common.back", "Home")}</Link>
          <Link to="/dashboard" className="btn btn-primary" data-testid="custom-trip-go-dashboard">{t("service_detail.view_dashboard", "View dashboard")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 px-5 md:px-10 mx-auto max-w-3xl pb-24">
      <div className="eyebrow mb-3">{t("header.nav_custom_trip", "Custom Trip")}</div>
      <h1 className="section-title text-5xl md:text-6xl mb-4">{t("custom_trip.title", "Design your trip.")}</h1>
      <p className="text-muted text-base mb-10 max-w-xl">{t("custom_trip.subtitle", "Tell us where, when, and your vibe. Our concierge curates the rest.")}</p>

      <form onSubmit={submit} className="space-y-8" data-testid="custom-trip-form">
        <div>
          <div className="eyebrow mb-3">{t("custom_trip.destinations_label", "Destinations")}</div>
          <div className="text-xs text-muted mb-3">{t("custom_trip.destinations_hint", "Pick one or more.")}</div>
          <div className="flex flex-wrap gap-2">
            {DESTINATIONS.map((d) => (
              <button type="button" key={d} onClick={() => toggle("destinations", d)}
                      className={`tab-pill ${form.destinations.includes(d) ? "active" : ""}`}
                      data-testid={`ct-dest-${d}`}>{d}</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label>
            <div className="eyebrow mb-2">{t("custom_trip.dates_start", "Start date")}</div>
            <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="input-bare" data-testid="ct-start"/>
          </label>
          <label>
            <div className="eyebrow mb-2">{t("custom_trip.dates_end", "End date")}</div>
            <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="input-bare" data-testid="ct-end"/>
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.flexible_dates} onChange={e => setForm({...form, flexible_dates: e.target.checked})} data-testid="ct-flex"/>
          <span>{t("custom_trip.flexible", "Dates are flexible")}</span>
        </label>

        <div className="grid sm:grid-cols-3 gap-4">
          <label>
            <div className="eyebrow mb-2">{t("custom_trip.guests_label", "Guests")}</div>
            <input type="number" min={1} value={form.guests} onChange={e => setForm({...form, guests: Number(e.target.value)})} className="input-bare" data-testid="ct-guests"/>
          </label>
          <label>
            <div className="eyebrow mb-2">{t("custom_trip.budget_from", "Budget from")}</div>
            <input type="number" min={0} value={form.budget_min} onChange={e => setForm({...form, budget_min: Number(e.target.value)})} className="input-bare" data-testid="ct-budget-min"/>
          </label>
          <label>
            <div className="eyebrow mb-2">{t("custom_trip.budget_to", "Budget to")}</div>
            <input type="number" min={0} value={form.budget_max} onChange={e => setForm({...form, budget_max: Number(e.target.value)})} className="input-bare" data-testid="ct-budget-max"/>
          </label>
        </div>

        <div>
          <div className="eyebrow mb-3">{t("custom_trip.style_label", "Trip style")}</div>
          <div className="flex flex-wrap gap-2">
            {TRIP_STYLES.map((s) => (
              <button type="button" key={s.value} onClick={() => toggle("trip_style", s.value)}
                      className={`tab-pill ${form.trip_style.includes(s.value) ? "active" : ""}`}>{s.label}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="eyebrow mb-3">{t("custom_trip.services_label", "Services you'd like")}</div>
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button type="button" key={s.value} onClick={() => toggle("requested_services", s.value)}
                      className={`tab-pill ${form.requested_services.includes(s.value) ? "active" : ""}`}>{s.label}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="eyebrow mb-3">{t("custom_trip.payment_label", "Preferred payment")}</div>
          <div className="flex gap-2 flex-wrap">
            {PAYMENT.map((p) => (
              <button type="button" key={p.value} onClick={() => setForm({...form, preferred_payment_method: p.value})}
                      className={`tab-pill ${form.preferred_payment_method === p.value ? "active" : ""}`}>{p.label}</button>
            ))}
          </div>
        </div>

        <label>
          <div className="eyebrow mb-2">{t("custom_trip.notes_label", "Describe your ideal trip")}</div>
          <textarea rows={5} value={form.raw_user_input} onChange={e => setForm({...form, raw_user_input: e.target.value})}
                    placeholder={t("custom_trip.notes_placeholder", "Tell us the vibe, who you're travelling with, any non-negotiables…")}
                    className="input-bare" data-testid="ct-raw"/>
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <input type="email"
                 placeholder={t("custom_trip.contact_email_label", "Contact email")}
                 value={form.contact_email}
                 onChange={e => setForm({...form, contact_email: e.target.value})}
                 className="input-bare" data-testid="ct-email"/>
          <input type="tel"
                 placeholder={t("custom_trip.contact_phone_label", "WhatsApp or phone (optional)")}
                 value={form.contact_phone}
                 onChange={e => setForm({...form, contact_phone: e.target.value})}
                 className="input-bare" data-testid="ct-phone"/>
        </div>

        {error && <div className="text-rose-300 text-sm">{error}</div>}

        <button type="submit" disabled={busy} className="btn btn-primary" data-testid="ct-submit">
          {busy ? t("custom_trip.sending", "Sending…") : t("custom_trip.submit", "Submit request")}
        </button>
      </form>
    </div>
  );
}
